/**
 * Shared, pure helpers for choosing a user's real Google profile photo from a
 * Firebase Auth user's `providerData`.
 *
 * A Firebase user can have MULTIPLE linked `google.com` providers (e.g. an
 * account re-linked/migrated over the years). Naively taking the first one
 * (`providerData.find(...)`) can pick a stale identity whose photo is Google's
 * grey "no photo" placeholder. These helpers pick conservatively: they return a
 * real photo only when there is exactly one unambiguous candidate, and never
 * return a known default/placeholder URL.
 *
 * NOTE: keep this in sync with the Cloud Functions copy at
 * `functions/Auth/googlePhoto.js`.
 */

// Best-effort markers for Google "no real photo" avatars:
//  - `/default-user` — the canonical Google default silhouette.
//  - `AOh14G`        — a legacy Google avatar token served as a grey placeholder
//                      for accounts without a real photo. Real modern photos use
//                      the `ACg8oc` / `ALV-Uj` tokens instead.
// Excluding these is deliberately conservative: the worst case is that we SKIP a
// photo update, never that we set a placeholder over a good photo.
const DEFAULT_AVATAR_MARKERS = ['/default-user', 'AOh14G'];

/**
 * True when a URL is empty or looks like a Google default/legacy placeholder.
 */
export function isDefaultOrLegacyAvatar(url) {
  if (typeof url !== 'string' || !url.trim()) return true;
  return DEFAULT_AVATAR_MARKERS.some((marker) => url.includes(marker));
}

/**
 * Pick the single real Google photo URL from a provider array, or null when
 * there is nothing safe to choose (no Google photo, only placeholders, or
 * multiple distinct real photos we cannot disambiguate). Never guesses.
 */
export function pickGooglePhotoURL(providerData) {
  const realPhotos = (Array.isArray(providerData) ? providerData : [])
    .filter(
      (p) =>
        p &&
        p.providerId === 'google.com' &&
        typeof p.photoURL === 'string' &&
        p.photoURL.trim()
    )
    .map((p) => p.photoURL.trim())
    .filter((url) => !isDefaultOrLegacyAvatar(url));

  const unique = Array.from(new Set(realPhotos));
  return unique.length === 1 ? unique[0] : null;
}
